import { useCallback, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useUiUplift } from "@/hooks/useUiUplift";
import { House } from "@phosphor-icons/react/House";
import { CalendarBlank } from "@phosphor-icons/react/CalendarBlank";
import { CreditCard } from "@phosphor-icons/react/CreditCard";
import { List } from "@phosphor-icons/react/List";
import { Users } from "@phosphor-icons/react/Users";
import { Megaphone } from "@phosphor-icons/react/Megaphone";
import { ChatCircle } from "@phosphor-icons/react/ChatCircle";
import { UserCircle } from "@phosphor-icons/react/UserCircle";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { CalendarCheck } from "@phosphor-icons/react/CalendarCheck";
import { ClipboardText } from "@phosphor-icons/react/ClipboardText";
import { FileText } from "@phosphor-icons/react/FileText";
import { Bell } from "@phosphor-icons/react/Bell";
import { GearSix } from "@phosphor-icons/react/GearSix";
import { BookOpenText } from "@phosphor-icons/react/BookOpenText";
import { BookmarkSimple } from "@phosphor-icons/react/BookmarkSimple";
import { HandHeart } from "@phosphor-icons/react/HandHeart";
import { SignOut } from "@phosphor-icons/react/SignOut";
import {
  MRHome,
  MRAnnouncements,
  MRProfile,
  MRHousehold,
  MRDirectory,
  MRGiving,
  MRGroups,
  MRMessages,
  MREvents,
  MRRegistrations,
  MRAttendance,
  MRPledges,
  MRRequests,
  MRPrayer,
  MRMedia,
  MRNotifications,
  MRPreferences,
  MRSchedule,
  MRAvailability,
  MRAdmin,
} from "@/components/icons/MemberIcons";
import { AppLayout } from "@/components/layout/AppLayout";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { usePersonRoleTags } from "@/hooks/usePersonRoleTags";
import { useMemberNotifications, useMemberPortalSettings } from "@/hooks/useMemberPortal";
import { Badge } from "@/components/ui/badge";
import { useResolveContextWithLoading } from "@/hooks/useCapability";
import { isPortalVisible } from "@/lib/capabilities/portal";
import { CAPABILITIES } from "@/lib/capabilities/registry";
import type { Capability, ResolveContext } from "@/lib/capabilities/types";

/* ─── Color palette for member portal sections ─── */
const SECTION_COLORS: Record<string, { text: string; bg: string; icon: string }> = {
  home:          { text: "text-[#8a6518]", bg: "bg-[#8a6518]/10", icon: "text-[#8a6518]" },
  announcements: { text: "text-[#d0505a]", bg: "bg-[#d0505a]/10", icon: "text-[#d0505a]" },
  profile:       { text: "text-[#2563a8]", bg: "bg-[#2563a8]/10", icon: "text-[#2563a8]" },
  household:     { text: "text-[#6d3fa0]", bg: "bg-[#6d3fa0]/10", icon: "text-[#6d3fa0]" },
  directory:     { text: "text-[#0e7c7b]", bg: "bg-[#0e7c7b]/10", icon: "text-[#0e7c7b]" },
  giving:        { text: "text-[#1a7a4c]", bg: "bg-[#1a7a4c]/10", icon: "text-[#1a7a4c]" },
  groups:        { text: "text-[#0891b2]", bg: "bg-[#0891b2]/10", icon: "text-[#0891b2]" },
  messages:      { text: "text-[#0891b2]", bg: "bg-[#0891b2]/10", icon: "text-[#0891b2]" },
  events:        { text: "text-[#0e7c7b]", bg: "bg-[#0e7c7b]/10", icon: "text-[#0e7c7b]" },
  registrations: { text: "text-[#c06030]", bg: "bg-[#c06030]/10", icon: "text-[#c06030]" },
  attendance:    { text: "text-[#2563a8]", bg: "bg-[#2563a8]/10", icon: "text-[#2563a8]" },
  pledges:       { text: "text-[#1a7a4c]", bg: "bg-[#1a7a4c]/10", icon: "text-[#1a7a4c]" },
  requests:      { text: "text-[#d49b0a]", bg: "bg-[#d49b0a]/10", icon: "text-[#d49b0a]" },
  prayer:        { text: "text-[#6d3fa0]", bg: "bg-[#6d3fa0]/10", icon: "text-[#6d3fa0]" },
  media:         { text: "text-[#c06030]", bg: "bg-[#c06030]/10", icon: "text-[#c06030]" },
  notifications: { text: "text-[#d0505a]", bg: "bg-[#d0505a]/10", icon: "text-[#d0505a]" },
  preferences:   { text: "text-[#8a6518]", bg: "bg-[#8a6518]/10", icon: "text-[#8a6518]" },
  schedule:      { text: "text-[#0e7c7b]", bg: "bg-[#0e7c7b]/10", icon: "text-[#0e7c7b]" },
  availability:  { text: "text-[#6d3fa0]", bg: "bg-[#6d3fa0]/10", icon: "text-[#6d3fa0]" },
};
const DEFAULT_COLOR = { text: "text-primary", bg: "bg-primary/10", icon: "text-primary" };

import { applyGivingStatementsGate, buildNavigation, filterMemberNav, type NavItem } from "./member-navigation";

/**
 * Group navigation items for the desktop sidebar by their `moreGroup` field.
 * Overview is pinned at the top with no group header.
 *
 * Previously the sidebar showed a single "VOLUNTEERING" section header on
 * the volunteer items and rendered everything else as a flat list — which
 * left Pledges, Requests, Media, Notifications, Preferences visually
 * orphaned under "Volunteering" even though they aren't volunteering items.
 * (Member portal audit 2026-05)
 */
const SIDEBAR_GROUP_ORDER: readonly string[] = ["Connect", "Give", "Serve", "My Account"];

function groupNavigation(items: NavItem[]): {
  overview: NavItem | undefined;
  groups: { name: string; items: NavItem[] }[];
} {
  const overview = items.find((i) => i.href === "/member");
  const rest = items.filter((i) => i.href !== "/member");
  const groups = SIDEBAR_GROUP_ORDER
    .map((name) => ({ name, items: rest.filter((i) => i.moreGroup === name) }))
    .filter((g) => g.items.length > 0);
  return { overview, groups };
}

/* ─── Mobile Bottom Navigation ─── */
function MobileBottomNav({
  isVolunteer,
  navigation,
  unreadCount = 0,
  navigate,
}: {
  isVolunteer: boolean;
  /** Capability-filtered nav (from MemberShell) used for the "More" sheet. */
  navigation: NavItem[];
  unreadCount?: number;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [moreOpen, setMoreOpen] = useState(false);

  // Subtle haptic feedback for mobile nav taps
  const haptic = useCallback(() => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(8);
    }
  }, []);

  // Hrefs surviving capability filtering — used to drop gated bottom tabs.
  const allowedHrefs = useMemo(
    () => new Set(navigation.map((item) => item.href)),
    [navigation],
  );

  const bottomTabs = useMemo(() => {
    const tabs: NavItem[] = [
      { label: "Home",   href: "/member",        icon: House,         colorKey: "home" },
      { label: "Events", href: "/member/events",  icon: CalendarBlank, colorKey: "events" },
    ];
    if (isVolunteer) {
      tabs.push({ label: "Schedule", href: "/member/schedule", icon: CalendarCheck, colorKey: "schedule" });
    }
    // "Give" → /member/giving (statements/history). Shown only when it survives nav
    // filtering — now gated by allow_giving_statements, not the payments capability. (GIV-5)
    if (allowedHrefs.has("/member/giving")) {
      tabs.push({ label: "Give", href: "/member/giving", icon: CreditCard, colorKey: "giving" });
    }
    return tabs;
  }, [isVolunteer, allowedHrefs]);

  const bottomHrefs = new Set(bottomTabs.map((t) => t.href));
  const moreItems = navigation.filter((item) => !bottomHrefs.has(item.href));

  return (
    <>
      {/* ── Bottom tab bar ── */}
      <nav aria-label="Member sections" className="member-mobile-nav fixed inset-x-0 bottom-0 z-40 flex items-stretch border-t border-border/30 bg-background pb-[env(safe-area-inset-bottom)] md:hidden">
        {bottomTabs.map((tab) => {
          const Icon = tab.icon;
          const colors = SECTION_COLORS[tab.colorKey] ?? DEFAULT_COLOR;
          return (
            <NavLink
              key={tab.href}
              to={tab.href}
              end={tab.href === "/member"}
              className="flex-1"
              onClick={haptic}
            >
              {({ isActive }) => (
                <div
                  className={cn(
                    "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold transition-all",
                    isActive ? colors.text : "text-muted-foreground/60",
                  )}
                >
                  <div className={cn(
                    "flex items-center justify-center rounded-full px-4 py-1.5 transition-all",
                    isActive ? `${colors.bg} shadow-sm` : "",
                  )}>
                    <Icon weight={isActive ? "duotone" : "regular"} className="h-5 w-5" />
                  </div>
                  <span className={cn("transition-all", isActive ? "opacity-100" : "opacity-70")}>
                    {tab.label}
                  </span>
                </div>
              )}
            </NavLink>
          );
        })}

        {/* More button */}
        <button
          type="button"
          onClick={() => { haptic(); setMoreOpen(true); }}
          className="relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold text-muted-foreground/60 transition-all"
        >
          <div className="flex items-center justify-center rounded-full px-4 py-1.5">
            <List weight="regular" className="h-5 w-5" />
          </div>
          <span className="opacity-70">More</span>
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute right-1/4 top-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </button>
      </nav>

      {/* ── More sheet (bottom drawer) — enhanced mobile UX ── */}
      <Sheet open={moreOpen} onOpenChange={(open) => {
        if (open && typeof navigator !== "undefined" && "vibrate" in navigator) {
          navigator.vibrate([5, 10, 5]);
        }
        setMoreOpen(open);
      }}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl">
          <SheetHeader className="text-left">
            <SheetTitle className="text-lg">More</SheetTitle>
            <SheetDescription>
              Access all sections of the member portal.
            </SheetDescription>
          </SheetHeader>

          {/* Group items by moreGroup for section headers */}
          {(() => {
            const groups = ["Connect", "Give", "Serve", "My Account"] as const;
            const adminItem = (
              <button
                key="admin-back"
                onClick={() => { navigate("/today"); setMoreOpen(false); }}
                className="member-more-tile flex flex-col items-center gap-2.5 rounded-xl border border-border/40 bg-muted/30 p-4 text-center transition-all active:scale-95 text-muted-foreground hover:border-border hover:bg-muted/50 min-h-[100px]"
              >
                <div className="flex h-14 w-14 items-center justify-center drop-shadow-md">
                  <MRAdmin className="h-12 w-12" />
                </div>
                <span className="text-xs font-semibold leading-tight">
                  Admin
                </span>
              </button>
            );

            return (
              <div className="mt-4 space-y-5">
                {groups.map((groupName) => {
                  const groupItems = moreItems.filter((item) => item.moreGroup === groupName);
                  if (groupItems.length === 0) return null;
                  return (
                    <div key={groupName}>
                      <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        {groupName}
                      </p>
                      <div className="grid grid-cols-2 min-[400px]:grid-cols-3 gap-3">
                        {groupItems.map((item) => {
                          const PRIcon = item.prIcon;
                          const colors = SECTION_COLORS[item.colorKey] ?? DEFAULT_COLOR;
                          return (
                            <NavLink
                              key={item.href}
                              to={item.href}
                              end={item.href === "/member"}
                              onClick={() => setMoreOpen(false)}
                            >
                              {({ isActive }) => (
                                <div
                                  className={cn(
                                    "member-more-tile flex flex-col items-center gap-2.5 rounded-xl border p-4 text-center transition-all active:scale-95 min-h-[100px]",
                                    isActive
                                      ? `${colors.bg} ${colors.text} border-current/20 font-semibold shadow-sm`
                                      : "border-border/40 text-muted-foreground hover:border-border hover:bg-muted/30",
                                  )}
                                >
                                  <div className={cn(
                                    "flex h-14 w-14 items-center justify-center transition-colors drop-shadow-md",
                                  )}>
                                    {PRIcon ? (
                                      <PRIcon className="h-12 w-12" />
                                    ) : (
                                      <item.icon weight="duotone" className={cn("h-8 w-8", colors.icon)} />
                                    )}
                                  </div>
                                  <span className="text-xs font-semibold leading-tight">
                                    {item.label}
                                  </span>
                                </div>
                              )}
                            </NavLink>
                          );
                        })}
                        {/* Add admin back button in My Account group */}
                        {groupName === "My Account" && adminItem}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ─── Main Shell ─── */
export function MemberShell() {
  const { data: person } = useCurrentPerson();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: roleTags = [] } = usePersonRoleTags(person?.id);
  const isVolunteer = roleTags.includes("Volunteer");
  // Resolve capability context ONCE (roles/tier/toggles/flags). MemberShell may
  // render before billing/roles load; the resolved context defaults tier to
  // "free" during that window, which would transiently hide tier-gated entries
  // (Directory, Giving, Groups, Pledges, Prayer Wall) until state resolves. To
  // avoid hiding-then-popping, mirror CapabilityRouteGate: while `isLoading`,
  // show the full unfiltered nav and only apply the capability filter once the
  // billing/roles/toggle state has loaded. Defaults are mostly enabled, so any
  // entry an org explicitly disabled only briefly shows before being filtered.
  const { ctx: resolveCtx, isLoading: capsLoading } = useResolveContextWithLoading();
  const { data: portalSettings } = useMemberPortalSettings();
  const navigation = useMemo(
    () => {
      const full = buildNavigation(isVolunteer);
      const filtered = capsLoading ? full : filterMemberNav(full, resolveCtx);
      // Giving (statements/history) is gated by allow_giving_statements, NOT the
      // online_giving payments capability — payments-off churches keep it. (GIV-5)
      return applyGivingStatementsGate(filtered, portalSettings?.allow_giving_statements);
    },
    [isVolunteer, resolveCtx, capsLoading, portalSettings?.allow_giving_statements],
  );
  const sidebarGroups = useMemo(() => groupNavigation(navigation), [navigation]);
  const uiVariant = useUiUplift();
  const { data: notifications = [] } = useMemberNotifications(person?.id);
  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications],
  );

  return (
    <AppLayout showSidebar={false}>
      <div className="member-shell" data-ui={uiVariant}>
        <div className={cn("grid gap-6", !isMobile && "lg:grid-cols-[220px_1fr]")}>
          {/* ── Desktop sidebar ── */}
          {!isMobile && (
            <nav aria-label="Member sections" className="member-desktop-nav flex flex-col gap-0.5 sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto pb-6">
              {/* Back to Admin */}
              <Button
                variant="ghost"
                className="w-full justify-start gap-2.5 text-xs font-medium text-muted-foreground hover:text-foreground mb-3 pb-3 rounded-none border-b border-border/20"
                onClick={() => navigate("/today")}
                title="Return to admin dashboard"
              >
                <SignOut weight="duotone" className="h-3.5 w-3.5" />
                Back to Admin
              </Button>

              {/* Overview pinned at top, no group header */}
              {sidebarGroups.overview && (
                <NavLink
                  key={sidebarGroups.overview.href}
                  to={sidebarGroups.overview.href}
                  end
                >
                  {({ isActive }) => {
                    const overview = sidebarGroups.overview!;
                    const Icon = overview.icon;
                    const colors = SECTION_COLORS[overview.colorKey] ?? DEFAULT_COLOR;
                    return (
                      <div
                        className={cn(
                          "member-nav-item flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-all cursor-pointer",
                          isActive
                            ? `${colors.bg} ${colors.text} font-semibold`
                            : "font-medium text-muted-foreground/80 hover:text-foreground hover:bg-muted/30",
                        )}
                      >
                        <Icon
                          weight={isActive ? "duotone" : "regular"}
                          className={cn("h-4 w-4 shrink-0", isActive && colors.icon)}
                        />
                        {overview.label}
                      </div>
                    );
                  }}
                </NavLink>
              )}

              {/* Grouped sections — Connect, Give, Serve, My Account */}
              {sidebarGroups.groups.map((group) => (
                <div key={group.name}>
                  <p className="mb-1 mt-4 px-3 text-[9px] font-bold uppercase tracking-widest text-muted-foreground/50">
                    {group.name}
                  </p>
                  {group.items.map((item) => (
                    <NavLink key={item.href} to={item.href}>
                      {({ isActive }) => {
                        const Icon = item.icon;
                        const colors = SECTION_COLORS[item.colorKey] ?? DEFAULT_COLOR;
                        return (
                          <div
                            className={cn(
                              "member-nav-item flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] transition-all cursor-pointer",
                              isActive
                                ? `${colors.bg} ${colors.text} font-semibold`
                                : "font-medium text-muted-foreground/80 hover:text-foreground hover:bg-muted/30",
                            )}
                          >
                            <Icon
                              weight={isActive ? "duotone" : "regular"}
                              className={cn("h-4 w-4 shrink-0", isActive && colors.icon)}
                            />
                            {item.label}
                          </div>
                        );
                      }}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          )}

          {/* ── Main content area ── */}
          <div key={location.pathname} className={cn("min-w-0 member-page-enter", isMobile && "pb-24")}>
            <Outlet />
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      {isMobile && <MobileBottomNav isVolunteer={isVolunteer} navigation={navigation} unreadCount={unreadCount} navigate={navigate} />}
    </AppLayout>
  );
}
